// Vista de lista genérica (§ 3.2): cabecera con búsqueda y "+ Nuevo",
// tabla con columnas de la definición del módulo, filas → ficha.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef, FieldDef, ModuleDef } from "../modules/registry.tsx";
import { LEAD_STATUS_COLOR } from "../modules/registry.tsx";
import { useCreate, useList, type Row } from "../lib/resource.ts";
import { useMe } from "../lib/auth.ts";
import { getCountry } from "../lib/country.ts";
import { applyDynamicOptions, useCustomFields } from "../lib/customFields.ts";
import { Plus } from "lucide-react";
import { useBusinessUnits } from "./BusinessUnitSelect.tsx";
import { LocalCombobox } from "./LocalCombobox.tsx";
import { SearchInput } from "./SearchInput.tsx";
import { formatDate, formatMoney } from "../lib/format.ts";
import { Button, Card, DotBadge, EmptyState, ErrorState, Modal, Select, TableSkeleton } from "./ui.tsx";
import { buildPayload, RecordForm, validate, type Values } from "./RecordForm.tsx";
import { toast } from "./toast.tsx";
import { ApiError, api } from "../lib/api.ts";

const PAGE_SIZE = 25;

export function ListView({ mod, extraActions }: { mod: ModuleDef; extraActions?: JSX.Element | ((filters: Record<string, string>) => JSX.Element) }): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState("");            // texto del input
  const [search, setSearch] = useState("");  // aplicado al servidor (debounced)
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filterOptions = useQuery<Record<string, Array<string | { value: string; label: string }>>>({
    queryKey: ["filter-options", mod.listFilters?.optionsPath],
    queryFn: () => api.get(`/${mod.listFilters!.optionsPath}`),
    enabled: Boolean(mod.listFilters),
    staleTime: 60_000,
  });
  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const list = useList(mod.key, {
    page, pageSize: PAGE_SIZE, q: search,
    ...(Object.keys(activeFilters).length ? { extra: activeFilters } : {}),
  });
  const create = useCreate(mod.key, mod.singular);
  const me = useMe();
  const readOnly = Boolean(me.data?.readOnly);
  const customFields = useCustomFields(mod.key);
  // Campo "País" solo para usuarios con AMBOS países y en módulos segmentados (no productos).
  const showCountry = Boolean(me.data?.countries?.co && me.data?.countries?.pe) && mod.key !== "products";
  const countryField: FieldDef = { name: "countryCode", label: "País", type: "enum", options: ["CO", "PE"], required: true, section: mod.fields[0]?.section ?? "Datos" };
  const formFields = [
    ...(showCountry ? [countryField] : []),
    ...applyDynamicOptions(mod.fields, customFields.data ?? []),
    ...(customFields.data ?? []),
  ];
  const openCreate = (): void => {
    setValues(showCountry ? { countryCode: getCountry() === "PE" ? "PE" : "CO" } : {});
    setErrors({});
    setCreating(true);
  };
  // Si el módulo tiene campo de unidad de negocio, pintamos el nombre con su color.
  const hasBU = mod.fields.some((f) => f.type === "businessunit");
  const buColors = useBusinessUnits();
  const buMap = new Map((hasBU ? buColors.data?.rows ?? [] : []).map((u) => [u.id, u.color]));
  const [creating, setCreating] = useState(false);
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Debounce de la búsqueda: espera a que el usuario deje de teclear.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const submit = (): void => {
    const errs = validate(formFields, values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    create.mutate(buildPayload(formFields, values) as Row, {
      onSuccess: (r) => {
        setCreating(false);
        setValues({});
        toast.success(`${mod.nav} creado.`);
        const created = (r[mod.singular] as Row | undefined) ?? undefined;
        const id = created?.["id"];
        if (typeof id === "string") navigate(`/${mod.key}/${id}`);
      },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear."),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="page-title shrink-0">{mod.nav}</h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {(mod.listFilters?.filters ?? []).map((f) => {
            const opts = filterOptions.data?.[f.key] ?? [];
            const setF = (v: string): void => { setFilters((prev) => ({ ...prev, [f.param]: v })); setPage(1); };
            // Muchas opciones de texto → buscador; pocas → desplegable normal.
            if (opts.every((o) => typeof o === "string") && opts.length > 25) {
              return (
                <div key={f.param} className="w-56">
                  <LocalCombobox value={filters[f.param] ?? ""} options={opts as string[]} onChange={setF} placeholder={`${f.label}: todos`} />
                </div>
              );
            }
            return (
              <Select
                key={f.param}
                className="w-auto min-w-40"
                value={filters[f.param] ?? ""}
                onChange={(e) => setF(e.target.value)}
              >
                <option value="">{f.label}: todos</option>
                {opts.map((o) => {
                  const v = typeof o === "string" ? o : o.value;
                  const l = typeof o === "string" ? o : o.label;
                  return <option key={v} value={v}>{l}</option>;
                })}
              </Select>
            );
          })}
          <SearchBox mod={mod} q={q} setQ={setQ} search={search} />
          {typeof extraActions === "function" ? extraActions(filters) : extraActions}
          {!readOnly && (
            <Button variant="primary" onClick={openCreate}>
              <Plus size={16} strokeWidth={2} /> Nuevo
            </Button>
          )}
        </div>
      </div>

      {!creating && (
        <Card>
          {list.isLoading ? (
            <TableSkeleton cols={mod.columns.length} />
          ) : list.isError ? (
            <ErrorState onRetry={() => void list.refetch()} />
          ) : rows.length === 0 ? (
            search ? (
              <EmptyState title="Sin resultados" body={`No se encontró "${search}".`} />
            ) : (
              <EmptyState
                title="Aún no hay registros"
                body={readOnly ? "No hay registros para mostrar." : "Crea el primero o importa desde un archivo."}
                {...(!readOnly ? { action: <Button variant="primary" onClick={openCreate}><Plus size={16} strokeWidth={2} /> Nuevo</Button> } : {})}
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-canvas">
                    {mod.columns.map((c) => <th key={c.name} className="th">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={String(r["id"])}
                      className="hover:bg-surface-hover cursor-pointer transition-colors"
                      onClick={() => navigate(`/${mod.key}/${String(r["id"])}`)}
                    >
                      {mod.columns.map((c) => (
                        <td key={c.name} className="td">
                          <Cell col={c} row={r} title={c.primary ? mod.titleField(r) : undefined}
                            {...(c.primary && hasBU ? { color: buMap.get(String(r["businessUnitId"] ?? "")) } : {})} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      {!creating && total > 0 && (
        <div className="flex items-center justify-between text-xs text-ink-secondary">
          <span>{from}–{to} de {total} {mod.nav.toLowerCase()}</span>
          <div className="flex items-center gap-2">
            <Button small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Anterior</Button>
            <span className="text-ink-muted">Página {page} de {totalPages}</span>
            <Button small disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Siguiente ›</Button>
          </div>
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={`Nuevo · ${mod.nav}`}
        wide
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancelar</Button>
            <Button variant="primary" onClick={submit} disabled={create.isPending}>
              {create.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </>
        }
      >
        <RecordForm fields={formFields} initial={values} errors={errors} onChange={setValues} />
      </Modal>
    </div>
  );
}

/** Buscador con sugerencias predictivas: al teclear muestra registros parecidos
 *  (mismo buscador del servidor: sin acentos, por palabras) para saltar directo. */
function SearchBox({ mod, q, setQ, search }: { mod: ModuleDef; q: string; setQ: (v: string) => void; search: string }): JSX.Element {
  const navigate = useNavigate();
  const [focused, setFocused] = useState(false);
  const sug = useList(mod.key, { q: search, pageSize: 8 }, focused && search.trim().length > 0);
  const rows = sug.data?.rows ?? [];
  const open = focused && search.trim().length > 0;

  const secondary = (r: Row): string => {
    for (const c of mod.columns) {
      if (c.primary) continue;
      const v = c.custom ? (r["customData"] as Record<string, unknown> | undefined)?.[c.name] : r[c.name];
      if (v != null && String(v).trim() !== "") return String(v);
    }
    return "";
  };

  return (
    <div className="relative w-56">
      <SearchInput
        value={q}
        onValueChange={setQ}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-80 right-0 card max-h-80 overflow-y-auto shadow-lg">
          {sug.isLoading ? (
            <div className="px-3 py-2 text-sm text-ink-muted">Buscando…</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ink-muted">Sin coincidencias.</div>
          ) : (
            rows.map((r) => (
              <button
                type="button"
                key={String(r["id"])}
                className="w-full text-left px-3 py-2 hover:bg-surface-hover border-b border-line last:border-0"
                onMouseDown={() => navigate(`/${mod.key}/${String(r["id"])}`)}
              >
                <div className="text-sm font-medium text-primary truncate">{mod.titleField(r)}</div>
                {secondary(r) && <div className="text-xs text-ink-muted truncate">{secondary(r)}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Cell({ col, row, title, color }: { col: ColumnDef; row: Row; title?: string | undefined; color?: string | undefined }): JSX.Element {
  const v = col.custom ? (row["customData"] as Record<string, unknown> | undefined)?.[col.name] : row[col.name];
  if (title !== undefined) return <span className={`font-medium ${color ? "" : "text-primary"}`} style={color ? { color } : undefined}>{title}</span>;
  if (col.name === "leadStatus" && typeof v === "string") {
    return <DotBadge label={v} hex={LEAD_STATUS_COLOR[v]} />;
  }
  if (col.type === "currency") return <>{formatMoney(v as string)}</>;
  if (col.type === "percent") return <>{v == null ? "—" : `${v}%`}</>;
  if (col.type === "bool") return <>{v ? "Sí" : "No"}</>;
  if (col.type === "date") return <>{formatDate(v as string)}</>;
  if (col.type === "enum" && typeof v === "string" && v) return <span className="text-ink-secondary">{v}</span>;
  return <>{v == null || v === "" ? "—" : String(v)}</>;
}
